import React, { useEffect, useContext } from 'react';

/**
 * Redesign landing page
 * Replaced ui-common files with gf-styled
 */
import Page from '@glowforge/gf-styled/components/contentful-views/page';
import { MadLibProvider } from '@glowforge/gf-styled/components';
import '@glowforge/gf-styled/initializers/contentful-page';
import { getContentByType } from '@glowforge/gf-styled/models/page';

import { fetchGlowforgeRebrandLandingPage } from '../api/contentful';
import ReferralInfoContext from '../context/ReferralInfoContext';
import { useMessageCenter } from '../context/MessageCenterProvider';
import useFeatureFlags from '../hooks/use-feature-flags';
import { pagePropType } from '../prop-types/contentful';

import CustomPageMetadata from '../components/custom-page-metadata/custom-page-metadata-rebrand';
import CallToActionBanner from '../components/call-to-action-banner/call-to-action-banner-rebrand';
import MeetTheFamily from '../components/meet-the-family/meet-the-family.widget';
import ReferralModal from '../components/referral-modal/referral-modal.widget';
import SignupModal from '../components/signup/signup-modal-rebrand';
import SignupWidget from '../components/signup/signup-widget-rebrand';

/* New Components */
import CatalogWidget from '../components/catalog/catalog.widget';
import TestimonialsWidget from '../components/testimonials/testimonials.widget';
import ExploreMaterialsWidget from '../components/explore-materials/explore-materials.widget';
import FeaturesDisplayWidget from '../components/features-display/features-display-rebrand.widget';
import NewsClips from '../components/news-clips/news-clips.widget';
import UniqueCreationsWidget from '../components/unique-creations/unique-creations.widget';
import SocialCards from '../components/social-cards/social-cards-rebrand.widget';

/* This is used to prevent layout shift when carousel cards change */
const CAROUSEL_MIN_HEIGHT_MOBILE = '350px';

const LandingPage = ({ page }) => {
  const customSections = {
    'meet-the-family': { component: MeetTheFamily },
    'call-to-action': { component: CallToActionBanner },
    'news-clips': { component: NewsClips },
    'social-cards': { component: SocialCards },
    'explore-materials': { component: ExploreMaterialsWidget },
    'features-display': { component: FeaturesDisplayWidget },
    'signup-form': { component: SignupWidget },
    'signup-modal': { component: SignupModal },
    'referral-modal-rebrand': { component: ReferralModal },
    'unique-creations': { component: UniqueCreationsWidget },
    'catalog-section-homepage': { component: CatalogWidget },
    testimonials: {
      component: TestimonialsWidget,
      props: { minHeightMobile: CAROUSEL_MIN_HEIGHT_MOBILE },
    },
  };

  /**
   * Grab the message sections instead of custom sections
   * since their component doesn't get mapped inside the
   * page
   */
  const messages = getContentByType(page?.fields?.sections, 'message');

  /**
   * Get referralInfo from context
   */
  const { referralInfo, ensureReferralInfo } = useContext(ReferralInfoContext);

  /**
   * Set up context for the message banner so we can update
   * it with the messages from contentful.
   */
  const messageCenter = useMessageCenter();

  const featureFlags = useFeatureFlags();

  /**
   * Load the Referral Info if needed
   */
  useEffect(() => {
    ensureReferralInfo();
    messageCenter.setMessages(messages);
  }, []);

  const {
    'madlib-segment-enabled': MADLIB_SEGMENT_ENABLED,
    'madlib-google-datastore-disabled': MADLIB_GOOGLE_DATASTORE_DISABLED,
  } = featureFlags;

  const attributions = {};
  if (referralInfo && referralInfo.code) {
    attributions.referralCode = referralInfo.code;
  }
  if (referralInfo && referralInfo.pl && referralInfo.pl.id) {
    attributions.promoLink = referralInfo.pl.id;
  }

  return (
    <MadLibProvider
      attributions={attributions}
      segmentEnabled={MADLIB_SEGMENT_ENABLED}
      googleDatastoreDisabled={MADLIB_GOOGLE_DATASTORE_DISABLED}>
      <CustomPageMetadata page={page} />
      <Page
        className='GlowforgeLandingPage'
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...page.fields}
        title={null}
        customSections={customSections}
      />
    </MadLibProvider>
  );
};

LandingPage.propTypes = {
  page: pagePropType.isRequired,
};

LandingPage.getInitialProps = async () => {
  const page = await fetchGlowforgeRebrandLandingPage();
  return { page };
};

export default LandingPage;
